import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useFeatureFormContext } from './feature-form-context'
import { useStyles } from './style'
import { ActionButton } from '@emerald-works-nova/components'
import { Box, Divider, Grid, Switch, Typography, useTheme } from '@mui/material'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { CONSTANTS } from '../../../util/constants'

/**
 * PENG-3812 — tenant-level Google Ads kill switch.
 *
 * Members (the Club tenant) is the only tenant that serves Google Ads, so the
 * switch is rendered there and nowhere else. The runtime is already Club-gated
 * at both ends — the API only injects ads on the Club tenant and the toolkit
 * hook returns false for every other tenant — so the flag is inert elsewhere
 * and this guard is about not offering a setting that does nothing.
 */
const GoogleAdsSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const [isLoading, setIsLoading] = useState(false)

  // Absent => ON. Matches the `!== false` predicate both consumers use, so a
  // tenant that has never had the field written keeps showing ads.
  const [value, setValue] = useState(tenant?.features?.googleAds !== false)

  const handleSwitch = () => {
    setValue(current => !current)
  }

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Google Ads configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Google Ads feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        googleAds: value
      }
    })
    setIsLoading(true)
  }

  if (tenant?.pk !== CONSTANTS.DEFAULT_CLUB_PK) {
    return null
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Box width='70%'>
              <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
                Google Ads
              </Typography>
              <Typography variant='body2' color='textSecondary' style={{ marginTop: spacing(1) }}>
                When off, no Google Ads are shown anywhere on this Toolkit — static ad slots,
                in-article ads and the ad-adjacent upgrade prompt. This overrides the Google Ads
                switch on individual content pages: content with ads enabled will still show none.
              </Typography>
            </Box>
            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch
                  color='primary'
                  data-test='google-ads-feature-toggle'
                  checked={value}
                  onChange={handleSwitch}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Box>

          <Divider style={{ marginBottom: spacing(1) }} />

          <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
            <ActionButton
              onClick={handleSave}
              disabled={isLoading}
              variant='primary'
              dataTest='google-ads-save-button'
              style={{ height: 'min-content' }}
            > Save
            </ActionButton>
          </Box>

        </Box>

      </Grid>

    </Grid>
  )
}

export default GoogleAdsSettings
